import { startTransition, useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { ClickType, GA4EventName, GA4FeatureCategory } from '@jsmdg/tracking';
import {
    Box,
    Breakpoint,
    Button,
    ButtonColor,
    ButtonShape,
    ButtonSize,
    ButtonType,
    ButtonVariant,
    ChevronIcon,
    IconHeadsetSrc,
    Image,
    SlidingDirections,
    useBreakpoint,
} from '@jsmdg/yoshi';
import BallonIcon from '@jsmdg/yoshi/dist/components/Icons/svg/ballon.svg';
import HalfClockIcon from '@jsmdg/yoshi/dist/components/Icons/svg/halfClock.svg';
import { trackPromoBannerSlideClick } from '../../helper/trackNavigation';
import { type HeaderContext } from '../../types';
import styles from './PromoBanner.module.scss';

const messages = defineMessages({
    textVarity: {
        defaultMessage: 'Persönlicher Einlöse-Service',
    },
    a11yNavButtonPrev: {
        defaultMessage: 'Banner-Navigationsbutton zurück',
    },
    a11yNavButtonNext: {
        defaultMessage: 'Banner-Navigationsbutton weiter',
    },
});

const PromoBannerItem = ({
    active,
    className,
    icon,
    text,
    wasActive,
}: {
    readonly icon: string;
    readonly text: string;
    readonly className?: string;
    readonly active: boolean;
    readonly wasActive: boolean;
}): JSX.Element => (
    <li
        className={classNames(className, styles.promoBannerItem, 'd-flex align-items-center')}
        data-active={active}
        data-was-active={wasActive}
    >
        <Image
            className={classNames(styles.icons, 'mr-xs-1x mr-0-5x')}
            src={icon}
            alt=""
            lazyLoad={false}
            width={20}
            height={20}
            fetchpriority="low"
        />
        <span>{text}</span>
    </li>
);

const PromoBanner = (): JSX.Element => {
    const { tenantConfig }: HeaderContext = useFragmentContext();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [slideDirection, setSlideDirection] = useState<SlidingDirections | null>(null);
    const isMobile = !useBreakpoint(Breakpoint.SM);
    const intl = useIntl();

    const promoBannerItems = [
        { icon: BallonIcon, text: tenantConfig.uspClaim },
        { icon: HalfClockIcon, text: tenantConfig.uspValidityText },
        {
            icon: IconHeadsetSrc,
            text: intl.formatMessage(messages.textVarity),
            className: 'd-sm-none d-md-flex',
        },
    ];

    const handleSlideChangeNext = useCallback(() => {
        startTransition(() => {
            setCurrentIndex(prevIndex => (prevIndex + 1) % promoBannerItems.length);
        });
    }, [promoBannerItems.length]);

    const handleSlideChangePrev = useCallback(() => {
        startTransition(() => {
            setCurrentIndex(prevIndex =>
                prevIndex > 0
                    ? (prevIndex - 1) % promoBannerItems.length
                    : promoBannerItems.length - 1,
            );
        });
    }, [promoBannerItems.length]);

    const handleBannerNavigationClick = async (direction: SlidingDirections): Promise<void> => {
        setSlideDirection(direction);

        if (direction === SlidingDirections.Previous) {
            handleSlideChangePrev();
        } else {
            handleSlideChangeNext();
        }

        await window.yieldToMainThread();
        trackPromoBannerSlideClick({
            eventName: GA4EventName.SlidePromotionItem,
            feature_category: GA4FeatureCategory.PromotionStatic,
            creative_name: 'USP_Banner',
            click_type:
                direction === SlidingDirections.Previous
                    ? ClickType.SlidePrevious
                    : ClickType.SlideNext,
        });
    };

    return (
        <Box
            boxWrapperClassName={styles.promoBannerWrapper}
            className={classNames(
                styles.promoBanner,
                'd-flex w-100 text-nowrap position-relative overflow-hidden',
            )}
        >
            <Button
                className={classNames('pb-1x d-sm-none')}
                a11yText={intl.formatMessage(messages.a11yNavButtonPrev)}
                color={ButtonColor.White}
                onClick={async () => handleBannerNavigationClick(SlidingDirections.Previous)}
                shape={ButtonShape.Icon}
                size={ButtonSize.Responsive}
                type={ButtonType.Button}
                variant={ButtonVariant.Ghost}
            >
                <ChevronIcon className={styles.iconPrev} />
            </Button>

            <ul
                className={classNames(
                    styles.promoBannerItemsContainer,
                    slideDirection &&
                        (slideDirection === SlidingDirections.Next
                            ? styles.slideNext
                            : styles.slidePrev),
                    'd-flex justify-content-xs-between w-100 m-0 p-0 position-relative',
                )}
            >
                {promoBannerItems.map((item, index) => (
                    <PromoBannerItem
                        key={item.text}
                        {...item}
                        active={currentIndex === index}
                        wasActive={
                            isMobile &&
                            (currentIndex === index + 1 ||
                                (currentIndex === 0 && index === promoBannerItems.length - 1))
                        }
                    />
                ))}
            </ul>
            <Button
                className={classNames('pb-1x d-sm-none')}
                a11yText={intl.formatMessage(messages.a11yNavButtonNext)}
                color={ButtonColor.White}
                onClick={async () => handleBannerNavigationClick(SlidingDirections.Next)}
                shape={ButtonShape.Icon}
                size={ButtonSize.Responsive}
                type={ButtonType.Button}
                variant={ButtonVariant.Ghost}
            >
                <ChevronIcon className={styles.iconNext} />
            </Button>
        </Box>
    );
};

export { PromoBanner };
